import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from './contexts/AuthContext';
import { StudyCafeAccessGuard } from './components/StudyCafeAccessGuard';
import { BusAccessGuard } from './components/BusAccessGuard';
import { DinnerAccessGuard } from './components/DinnerAccessGuard';
import { PrivacyPolicyRequiredGuide } from './components/PrivacyPolicyRequiredGuide';
import { ServiceUnavailableGuide } from './components/ServiceUnavailableGuide';
import { Home } from './pages/Home';
import { Selection } from './pages/Selection';
import { FixedSeatGuide } from './pages/StudyCafe/FixedSeatGuide';
import { StudyCafeAccessRestrictedGuide } from './pages/StudyCafe/AccessRestrictedGuide';
import { SeatSelection } from './pages/StudyCafe/SeatSelection';
import { TimeSelection } from './pages/StudyCafe/TimeSelection';
import { StudyCafeReservationComplete } from './pages/StudyCafe/ReservationCompletePage';
import { BusReservation } from './pages/Bus/BusReservation';
import { BusAccessRestrictedGuide } from './pages/Bus/AccessRestrictedGuide';
import { BusAlreadyReservedGuide } from './pages/Bus/AlreadyReservedGuide';
import { BusReservationComplete } from './pages/Bus/ReservationCompletePage';
import { DinnerReservation } from './pages/Dinner/DinnerReservation';
import { DinnerAccessRestrictedGuide } from './pages/Dinner/AccessRestrictedGuide';
import { DinnerAlreadyReservedGuide } from './pages/Dinner/AlreadyReservedGuide';
import { DinnerReservationComplete } from './pages/Dinner/ReservationCompletePage';
import { ForgotPassword } from './pages/ForgotPassword';
import { ReservationTimeRestrictedGuide } from './pages/Common/ReservationTimeRestrictedGuide';
import {
    CENTER_SERVICE_AVAILABILITY_STATUS,
    getCenterServiceAvailabilityStatus,
} from './utils/centerServiceAvailability';

function App() {
    const { isLoggedIn, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [currentTime, setCurrentTime] = useState(() => new Date());
    const [isPrivacyPolicyLoading, setIsPrivacyPolicyLoading] = useState(true);
    const [isPrivacyPolicyMissing, setIsPrivacyPolicyMissing] = useState(false);
    const availabilityStatus = getCenterServiceAvailabilityStatus(currentTime);
    const isFullCenterBlocked = availabilityStatus === CENTER_SERVICE_AVAILABILITY_STATUS.FULL_CENTER_BLOCKED;

    useEffect(() => {
        const timer = window.setInterval(() => {
            setCurrentTime(new Date());
        }, 30000);

        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (isFullCenterBlocked) {
            setIsPrivacyPolicyMissing(false);
            setIsPrivacyPolicyLoading(false);
            return;
        }

        let isMounted = true;

        const checkPrivacyPolicy = async () => {
            try {
                const response = await fetch('/api/center/privacy-policy/current', {
                    credentials: 'include',
                });

                if (!isMounted) {
                    return;
                }

                setIsPrivacyPolicyMissing(response.status === 204);
            } catch (error) {
                console.error('Failed to check privacy policy:', error);

                if (!isMounted) {
                    return;
                }

                setIsPrivacyPolicyMissing(false);
            } finally {
                if (isMounted) {
                    setIsPrivacyPolicyLoading(false);
                }
            }
        };

        void checkPrivacyPolicy();

        return () => {
            isMounted = false;
        };
    }, [isFullCenterBlocked]);

    useEffect(() => {
        // 로딩 완료 후, 로그인이 안 되어 있고 홈이 아니면 홈으로 리다이렉트
        if (
            !isFullCenterBlocked &&
            !isPrivacyPolicyMissing &&
            !isLoading &&
            !isLoggedIn &&
            location.pathname !== '/' &&
            location.pathname !== '/forgot-password'
        ) {
            navigate('/', { replace: true });
        }
    }, [isFullCenterBlocked, isPrivacyPolicyMissing, isLoading, isLoggedIn, location.pathname, navigate]);

    if (isFullCenterBlocked) {
        return <ServiceUnavailableGuide />;
    }

    if (isLoading || isPrivacyPolicyLoading) {
        return <div style={{ width: '100vw', height: '100vh', backgroundColor: '#ffffff' }} aria-hidden="true" />;
    }

    if (isPrivacyPolicyMissing) {
        return <PrivacyPolicyRequiredGuide />;
    }

    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/selection" element={<Selection />} />
            <Route path="/reservation-time-restricted" element={<ReservationTimeRestrictedGuide />} />
            {/* 스터디카페: 시간 선택 → 좌석 선택 순서 */}
            <Route path="/study-cafe/fixed-seat" element={<FixedSeatGuide />} />
            <Route path="/study-cafe/access-restricted" element={<StudyCafeAccessRestrictedGuide />} />
            <Route
                path="/study-cafe"
                element={
                    <StudyCafeAccessGuard>
                        <TimeSelection />
                    </StudyCafeAccessGuard>
                }
            />
            <Route
                path="/study-cafe/seat-selection"
                element={
                    <StudyCafeAccessGuard>
                        <SeatSelection />
                    </StudyCafeAccessGuard>
                }
            />
            <Route path="/study-cafe/complete" element={<StudyCafeReservationComplete />} />
            <Route
                path="/bus"
                element={
                    <BusAccessGuard>
                        <BusReservation />
                    </BusAccessGuard>
                }
            />
            <Route path="/bus/access-restricted" element={<BusAccessRestrictedGuide />} />
            <Route path="/bus/already-reserved" element={<BusAlreadyReservedGuide />} />
            <Route path="/bus/complete" element={<BusReservationComplete />} />
            <Route
                path="/dinner"
                element={
                    <DinnerAccessGuard>
                        <DinnerReservation />
                    </DinnerAccessGuard>
                }
            />
            <Route path="/dinner/access-restricted" element={<DinnerAccessRestrictedGuide />} />
            <Route path="/dinner/already-reserved" element={<DinnerAlreadyReservedGuide />} />
            <Route path="/dinner/complete" element={<DinnerReservationComplete />} />
        </Routes>
    );
}

export default App;
